import { formatNative, formatUsd } from './optionsDashboardUtils';
import { buildMoneyValuePresentation, getPnlToneClass } from './optionsVisualConfig';
import { buildStrategyFilterOptions, formatStrategyLabel } from './optionsStrategyFilterUtils';
import { formatLocalizedNumber } from './optionsLocaleFormatting';

export const OPEN_POSITIONS_CURRENCIES = ['usd', 'btc', 'eth'];
export const OPEN_POSITIONS_UNDERLYINGS = ['all', 'btc', 'eth', 'usdc'];
export const OPEN_POSITIONS_SORTS = ['dte', 'pnl_desc', 'pnl_asc', 'net_delta'];

const DTE_WARNING_DAYS = 30;
const DTE_URGENT_DAYS = 14;

const EXPIRATION_MONTHS = {
  en: ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'],
  pt: ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'],
};

export const formatExpirationDate = (isoString, language = 'en') => {
  if (!isoString) return '—';
  const date = new Date(isoString);
  const months = EXPIRATION_MONTHS[language] || EXPIRATION_MONTHS.en;
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = months[date.getUTCMonth()] || EXPIRATION_MONTHS.en[date.getUTCMonth()];
  const year = date.getUTCFullYear();
  return `${day} ${month} ${year}`;
};

export const formatOpenedDate = (isoString) => {
  if (!isoString) return '—';
  const date = new Date(isoString);
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(date).replace(',', ' ·');
};

export const computeDteDays = (expirationIso, now = new Date()) => {
  if (!expirationIso) return null;
  const expiration = new Date(expirationIso);
  const diff = expiration.getTime() - now.getTime();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

export const getDteTone = (dte) => {
  if (dte === null || dte === undefined) {
    return {
      labelClassName: 'border-white/10 bg-white/[0.04] text-white/70',
      valueClassName: 'text-white',
    };
  }

  if (dte <= DTE_URGENT_DAYS) {
    return {
      labelClassName: 'border-[#FF6B6B]/25 bg-[#FF6B6B]/12 text-[#FFC7C7]',
      valueClassName: 'text-[#FFD6D6]',
    };
  }

  if (dte <= DTE_WARNING_DAYS) {
    return {
      labelClassName: 'border-[#F0B90B]/25 bg-[#F0B90B]/12 text-[#F7D47A]',
      valueClassName: 'text-[#FFE29A]',
    };
  }

  return {
    labelClassName: 'border-white/10 bg-white/[0.04] text-white/70',
    valueClassName: 'text-white',
  };
};

export const getPnlClassName = (value) => getPnlToneClass(value);

export const formatSizeDisplay = (legs = []) => {
  if (!legs.length) return '—';
  const quantities = legs.map((leg) => Number(leg.quantity_remaining ?? leg.quantity_opened ?? 0));
  const min = Math.min(...quantities);
  const max = Math.max(...quantities);
  if (Math.abs(max - min) < 0.000001) {
    return `${formatLocalizedNumber(min, { minimumFractionDigits: min % 1 === 0 ? 0 : 2, maximumFractionDigits: 4 })} contracts`;
  }
  return `${formatLocalizedNumber(min, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}–${formatLocalizedNumber(max, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} contracts`;
};

export const formatOperationPnl = ({ selectedCurrency, nativeValue, usdValue, underlying }) => {
  const presentation = buildMoneyValuePresentation({
    selectedCurrency,
    nativeValue,
    usdValue,
    underlying,
    digits: 4,
    tone: 'pnl',
  });

  return {
    primary: presentation.primary,
    secondary: presentation.secondary,
    colorClassName: presentation.primaryToneClass,
    secondaryColorClassName: presentation.secondaryToneClass,
  };
};

export const formatFeesDisplay = ({ selectedCurrency, nativeValue, usdValue, underlying }) => {
  const normalizedNative = nativeValue === null || nativeValue === undefined ? nativeValue : -Math.abs(Number(nativeValue));
  const normalizedUsd = usdValue === null || usdValue === undefined ? usdValue : -Math.abs(Number(usdValue));
  return buildMoneyValuePresentation({
    selectedCurrency,
    nativeValue: normalizedNative,
    usdValue: normalizedUsd,
    underlying,
    digits: 4,
    tone: 'neutral',
  });
};

export const normalizeLegType = (leg) => {
  return `${leg.position_side || '—'} ${leg.option_type || 'OPTION'}`;
};

export const sortLegsForDisplay = (legs = []) => {
  return [...legs].sort((a, b) => {
    const strikeA = Number(a.strike || 0);
    const strikeB = Number(b.strike || 0);
    return strikeA - strikeB;
  });
};

export const buildStrikeSummary = (strategyLabel, legs = []) => {
  const orderedLegs = sortLegsForDisplay(legs);
  if (!orderedLegs.length) return [];

  if (strategyLabel === 'SHORT_STRANGLE' || strategyLabel === 'SHORT_STRADDLE') {
    const putLeg = orderedLegs.find((leg) => leg.option_type === 'PUT');
    const callLeg = orderedLegs.find((leg) => leg.option_type === 'CALL');
    return [putLeg, callLeg]
      .filter(Boolean)
      .map((leg) => ({
        key: `${leg.position_side}-${leg.option_type}-${leg.strike}`,
        label: normalizeLegType(leg),
        value: formatLocalizedNumber(Number(leg.strike || 0), { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
        quantity: leg.quantity_remaining ?? leg.quantity_opened ?? null,
        positionSide: leg.position_side,
      }));
  }

  return orderedLegs.map((leg) => ({
    key: `${leg.position_side}-${leg.option_type}-${leg.strike}`,
    label: normalizeLegType(leg),
    value: formatLocalizedNumber(Number(leg.strike || 0), { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
    quantity: leg.quantity_remaining ?? leg.quantity_opened ?? null,
    positionSide: leg.position_side,
  }));
};

export const getDirectionalBiasLabel = (operation) => {
  if (operation?.strategy_label === 'SHORT_STRANGLE' || operation?.strategy_label === 'SHORT_STRADDLE') {
    if (operation?.directional_bias === 'NEUTRAL') return 'BALANCED';
    if (operation?.directional_bias === 'BULLISH') return 'BULLISH BIAS';
    if (operation?.directional_bias === 'BEARISH') return 'BEARISH BIAS';
  }
  if (operation?.directional_bias === 'NEUTRAL') return 'BALANCED';
  if (operation?.directional_bias) return operation.directional_bias;
  if (operation?.strategy_label === 'LONG_CALL') return 'BULLISH';
  if (operation?.strategy_label === 'LONG_PUT') return 'BEARISH';
  return null;
};

export const getCampaignContext = ({ operation, campaignDetail }) => {
  if (!campaignDetail?.campaign) return null;
  const campaign = campaignDetail.campaign;
  const operations = [...(campaignDetail.operations || [])].sort((a, b) => new Date(a.opened_at || 0).getTime() - new Date(b.opened_at || 0).getTime());
  const previousOperation = operations.find((item) => item.id !== operation.id && item.status === 'CLOSED');
  const rollTypes = (campaignDetail.roll_links || [])
    .filter((link) => link.target_operation_id === operation.id)
    .flatMap((link) => link.roll_types || []);

  const previousMonthLabel = previousOperation?.expiration ? new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' }).format(new Date(previousOperation.expiration)).toUpperCase() : null;
  const compactRollSummary = previousOperation ? `${formatExpirationDate(previousOperation.expiration)} → ${formatExpirationDate(operation.expiration)}` : null;

  return {
    rolled: campaign.roll_link_count > 0,
    campaignId: campaign.id,
    operationCount: campaign.operation_count,
    rollLinkCount: campaign.roll_link_count,
    previousMonthLabel,
    compactRollSummary,
    campaignNetBase: campaign.campaign_net_pnl_base,
    campaignNetUsd: campaign.campaign_net_pnl_usd,
    operations,
    rollTypes,
  };
};

export const getEffectiveUnderlyingFilter = ({ selectedCurrency, underlyingFilter }) => {
  if (selectedCurrency === 'btc' || selectedCurrency === 'eth') {
    return selectedCurrency;
  }
  return underlyingFilter;
};

export const buildOpenPositionsStrategyOptions = ({ positions, selectedCurrency, underlyingFilter }) => {
  const filtered = filterOpenPositions({
    positions,
    selectedCurrency,
    underlyingFilter,
    strategyFilter: 'all',
  });

  return buildStrategyFilterOptions(filtered.map((position) => position?.strategyLabel));
};

export { formatStrategyLabel };

export const filterOpenPositions = ({ positions, selectedCurrency, underlyingFilter, strategyFilter }) => {
  const effectiveUnderlying = getEffectiveUnderlyingFilter({ selectedCurrency, underlyingFilter });

  return positions.filter((position) => {
    const underlyingMatches = effectiveUnderlying === 'all' || position.underlying.toLowerCase() === effectiveUnderlying;
    const strategyMatches = strategyFilter === 'all' || position.strategyLabel === strategyFilter;
    return underlyingMatches && strategyMatches;
  });
};

// Nullable-safe comparator helpers for sorting: missing values always sort last,
// regardless of direction, and are never silently coerced to 0.
const toNullableNumber = (value) => (value === null || value === undefined ? null : Number(value));

const compareNullableDesc = (a, b) => {
  if (a === null) return b === null ? 0 : 1;
  if (b === null) return -1;
  return b - a;
};

const compareNullableAsc = (a, b) => {
  if (a === null) return b === null ? 0 : 1;
  if (b === null) return -1;
  return a - b;
};

export const sortOpenPositions = ({ positions, sortBy, selectedCurrency }) => {
  const copy = [...positions];
  copy.sort((a, b) => {
    if (sortBy === 'pnl_desc' || sortBy === 'pnl_asc') {
      const valueA = toNullableNumber(selectedCurrency === 'usd' ? a.netPnlUsd : a.netPnlNative);
      const valueB = toNullableNumber(selectedCurrency === 'usd' ? b.netPnlUsd : b.netPnlNative);
      return sortBy === 'pnl_desc' ? compareNullableDesc(valueA, valueB) : compareNullableAsc(valueA, valueB);
    }

    if (sortBy === 'net_delta') {
      const valueA = toNullableNumber(a.netDelta);
      const valueB = toNullableNumber(b.netDelta);
      return compareNullableDesc(valueA === null ? null : Math.abs(valueA), valueB === null ? null : Math.abs(valueB));
    }

    const dteA = a.dte ?? Number.POSITIVE_INFINITY;
    const dteB = b.dte ?? Number.POSITIVE_INFINITY;
    return dteA - dteB;
  });
  return copy;
};
